import React from 'react';
import { Button } from '../components/ui/button';
import { GraduationCap, TrendingUp, FileText, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';

const CreditEducation = () => {
  const benefits = [
    'Understand how credit scores are calculated and what impacts them',
    'Learn proven strategies to improve your credit score',
    'Get personalized recommendations for your credit situation',
    'Master the art of using credit wisely and responsibly',
    'Develop long-term credit management skills',
    'Receive ongoing support and guidance'
  ];

  const topics = [
    {
      icon: FileText,
      title: 'Credit Score Basics',
      description: 'Learn what a credit score is, how it\'s calculated, and why it matters for your financial future.'
    },
    {
      icon: TrendingUp,
      title: 'Improvement Strategies',
      description: 'Discover proven techniques to raise your credit score and maintain good credit health.'
    },
    {
      icon: AlertCircle,
      title: 'Credit Mistakes to Avoid',
      description: 'Understand common credit pitfalls and how to avoid them to protect your score.'
    },
    {
      icon: GraduationCap,
      title: 'Smart Credit Usage',
      description: 'Learn how to use credit cards and loans effectively to build and maintain excellent credit.'
    }
  ];

  const creditFactors = [
    { factor: 'Payment History', percentage: '35%', description: 'On-time payments are crucial' },
    { factor: 'Credit Utilization', percentage: '30%', description: 'Keep balances low relative to limits' },
    { factor: 'Credit History Length', percentage: '15%', description: 'Longer history is better' },
    { factor: 'Credit Mix', percentage: '10%', description: 'Variety of credit types' },
    { factor: 'New Credit', percentage: '10%', description: 'Limit new applications' }
  ];

  const improvements = [
    'Pay all bills on time, every time',
    'Keep credit card balances below 30% of limits',
    'Don\'t close old credit accounts',
    'Limit new credit applications',
    'Dispute errors on your credit report',
    'Become an authorized user on a good account',
    'Pay down high balances strategically',
    'Set up automatic payments to never miss a due date'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-6 rounded-full shadow-lg">
              <GraduationCap className="h-16 w-16 text-[#0B8FD8]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold mb-6">
            Credit Education
          </h1>
          <p className="text-lg md:text-xl text-[#2C3E50] leading-relaxed mb-8">
            Master the fundamentals of credit management with personalized education and proven strategies 
            to improve your credit score and build a strong financial foundation.
          </p>
          <Button 
            className="bg-[#0B8FD8] hover:bg-[#0973B0] text-white px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Start Your Credit Journey
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Topics Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            What You'll Learn
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {topics.map((topic, index) => {
              const Icon = topic.icon;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="bg-[#C4E5F4] p-4 rounded-full">
                      <Icon className="h-10 w-10 text-[#0B8FD8]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                    {topic.title}
                  </h3>
                  <p className="text-base text-[#2C3E50]">
                    {topic.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credit Factors Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Understanding Your Credit Score
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <p className="text-lg text-[#2C3E50] text-center mb-8">
              Your credit score is calculated based on five key factors:
            </p>
            <div className="space-y-6">
              {creditFactors.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-[#C4E5F4] rounded-lg">
                  <div className="flex-1">
                    <h3 className="text-lg font-serif text-[#0A2540] font-bold">
                      {item.factor}
                    </h3>
                    <p className="text-sm text-[#2C3E50]">{item.description}</p>
                  </div>
                  <div className="text-3xl font-bold text-[#0B8FD8] ml-4">
                    {item.percentage}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Improvement Strategies Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            Proven Credit Improvement Strategies
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {improvements.map((strategy, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] rounded-xl">
                <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                <p className="text-base text-[#2C3E50] font-medium">{strategy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Why Credit Education Matters
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                  <p className="text-lg text-[#2C3E50]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            The Impact of Good Credit
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#C4E5F4] rounded-xl p-8 text-center">
              <h3 className="text-2xl font-serif text-[#0A2540] font-bold mb-4">
                Lower Interest Rates
              </h3>
              <p className="text-base text-[#2C3E50]">
                Save thousands on loans and mortgages with better rates.
              </p>
            </div>
            
            <div className="bg-[#C4E5F4] rounded-xl p-8 text-center">
              <h3 className="text-2xl font-serif text-[#0A2540] font-bold mb-4">
                Better Approvals
              </h3>
              <p className="text-base text-[#2C3E50]">
                Increase your chances of approval for credit cards, loans, and rentals.
              </p>
            </div>
            
            <div className="bg-[#C4E5F4] rounded-xl p-8 text-center">
              <h3 className="text-2xl font-serif text-[#0A2540] font-bold mb-4">
                Financial Freedom
              </h3>
              <p className="text-base text-[#2C3E50]">
                Access better financial opportunities and build lasting wealth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B8FD8] py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-bold mb-6">
            Ready to Transform Your Credit?
          </h2>
          <p className="text-lg text-white mb-8">
            Schedule your free credit education consultation and start building better credit today.
          </p>
          <Button 
            className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CreditEducation;
